/**
 * Hook centralisé pour tous les dashboards chefs de département.
 * Charge employees, tasks, projects, objectifs depuis l'API selon le dept_id.
 */
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from './useAuth';

const COLORS_CYCLE = ['#C9A84C','#42a5f5','#66bb6a','#ab47bc','#ff9800','#26c6da','#ef5350','#64b5f6'];
const colorFor = (str) => COLORS_CYCLE[(str || '').length % COLORS_CYCLE.length];

export function mapEmployee(emp) {
  const u = emp.user || {};
  const dept = u.departement || emp.departement || {};
  const filiale = u.filiale || emp.filiale || {};
  const rawPrenom = u.prenom || emp.prenom || 'N/A';
  let rawNom = u.nom || emp.nom || 'N/A';
  // Si nom contient déjà le prénom (ex: "Sylvio MONGBO"), extraire seulement le nom de famille
  if (rawPrenom !== 'N/A' && rawNom.startsWith(rawPrenom + ' ')) {
    rawNom = rawNom.slice(rawPrenom.length + 1).trim();
  }
  const prenom = rawPrenom;
  const nom = rawNom;
  return {
    id: emp.id,
    _id: emp.id,
    user_id: u.id || emp.user_id,
    av: ((prenom[0] || 'X') + (nom[0] || 'X')).toUpperCase(),
    prenom,
    nom,
    poste: u.poste || emp.poste || '—',
    dept: dept?.nom || '—',
    filiale: filiale?.nom || '—',
    color: emp.color || colorFor(prenom + nom),
    contrat: emp.contrat || emp.type_contrat || 'CDI',
    entree: emp.date_embauche
      ? new Date(emp.date_embauche).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
      : '—',
    heureLimite: emp.heure_limite || '08:30',
    arrivee: emp.heure_arrivee ? emp.heure_arrivee.slice(0, 5) : '—',
    present: !!emp.present,
    score: emp.score_performance || 0,
    scoreLabel: emp.score_performance >= 8 ? 'Excellent' : emp.score_performance >= 6 ? 'Bien' : 'À améliorer',
    tachesQuot: emp.taches_quotidiennes || 0,
    tachesDone: emp.taches_completees || 0,
    assignedTasks: [],
    objectifs: (emp.objectifs || []).map(o => ({
      id: o.id,
      titre: o.titre || o.title || '—',
      description: o.description || '',
      progression: o.progression || 0,
      statut: o.statut || 'en_cours',
      commentaire: o.commentaire || '',
    })),
    pendingTasks: [],
    criteres: emp.criteres || {},
    pointage: [],
    // 🔧 AJOUTÉ : role pour filtrage frontend
    role: u.role || emp.role || 'employe',
    departement_id: dept?.id || emp.departement_id || null,
  };
}

export function mapTask(t) {
  return {
    id: t.id,
    _id: t.id,
    text: t.titre || t.title || t.text || '—',
    titre: t.titre || t.title || '—',
    done: !!t.done,
    prio: t.priority || t.priorite || 'normale',
    priority: t.priority || t.priorite || 'normale',
    categorie: t.category || t.categorie || 'Admin',
    employee_id: t.employee_id || t.assigned_to,
    date: t.deadline ? t.deadline.slice(0, 10) : new Date().toISOString().slice(0, 10),
    project_id: t.project_id,
    statut: t.statut || (t.done ? 'termine' : 'a_faire'),
    progression: t.progression || (t.done ? 100 : 0),
    description: t.description || '',
    dateEcheance: t.deadline ? t.deadline.slice(0, 10) : '',
  };
}

export function mapProject(p) {
  return {
    id: p.id,
    _id: p.id,
    nom: p.name || p.nom || '—',
    name: p.name || p.nom || '—',
    status: p.status || p.statut || 'planifie',
    statut: p.status || p.statut || 'planifie',
    av: p.progress || 0,
    progress: p.progress || 0,
    budget: p.budget || 0,
    deadline: p.end_date ? p.end_date.slice(5).split('-').reverse().join('/') : (p.deadline || '—'),
    description: p.description || '',
  };
}

export default function useDeptDashboard({ deptId, deptIdAlt, withProjects = false, roleFilter = null }) {
  const { user } = useAuth(); // 🔧 Récupération user connecté
  const [employes, setEmployes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // 🔧 DEPT_ID dynamique : priorité au param, sinon celui de l'utilisateur connecté
      const effectiveDeptId = deptId || user?.departement_id || user?.employee?.departement_id;
      const deptParam = effectiveDeptId ? { departement_id: effectiveDeptId } : {};
      const deptParamAlt = deptIdAlt ? { departement_id: deptIdAlt } : deptParam;

      // 🔧 Filtre role côté API
      const empFilters = { ...deptParam, ...(roleFilter && { role: roleFilter }) };

      const calls = [
        api.employees.getAll(empFilters).catch(() => ({ employees: [] })),
        api.tasks.getAll(deptParamAlt).catch(() => ({ tasks: [] })),
      ];
      if (withProjects) {
        calls.push(api.projects.getAll(deptParamAlt).catch(() => ({ projects: [] })));
      }

      const [empRes, tasksRes, projRes] = await Promise.all(calls);

      const rawEmployees = empRes?.employees || [];
      const allTasks = (tasksRes?.tasks || []).map(mapTask);
      const allProjects = withProjects ? (projRes?.projects || []).map(mapProject) : [];

      // Mapping + association tâches
      let mappedEmployees = rawEmployees.map(emp => {
        const base = mapEmployee(emp);
        // Matcher employee_id = user_id (tâches créées via TdbIndividuel) OU employee_id = emp.id (tâches créées par le chef)
        const empTasks = allTasks.filter(t => t.employee_id === base.user_id || t.employee_id === base.id);
        const today = new Date().toISOString().slice(0, 10);
        const todayTasks = empTasks.filter(t => t.date === today);

        return {
          ...base,
          tachesQuot: todayTasks.length || base.tachesQuot,
          tachesDone: todayTasks.filter(t => t.done).length || base.tachesDone,
          assignedTasks: empTasks,
          pendingTasks: empTasks.filter(t => !t.done).slice(0, 5).map(t => ({
            id: t.id,
            titre: t.text,
            prio: t.prio,
            priorite: t.prio,
          })),
        };
      });

      // 🔧 FILTRAGE FRONTEND : ne garder que les employés avec le bon role
      if (roleFilter) {
        mappedEmployees = mappedEmployees.filter(emp => emp.role === roleFilter);
      }

      setEmployes(mappedEmployees);
      setTasks(allTasks);
      if (withProjects) setProjects(allProjects);
    } catch (err) {
      setError(err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [deptId, deptIdAlt, withProjects, roleFilter, user]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // CRUD Tâches
  const normalizeTaskPayload = useCallback((data = {}) => {
    const today = new Date().toISOString().slice(0, 10);
    const title = data.titre || data.title || data.text || '';

    return {
      titre: title.trim(),
      description: data.description || data.text || '',
      employee_id: data.employee_id ?? data.assigned_to ?? null,
      project_id: data.project_id ?? null,
      priority: data.priority || data.prio || 'normale',
      category: data.category || data.categorie || 'Admin',
      type_periode: data.type_periode || 'quotidienne',
      date_tache: data.date_tache || data.date || today,
      semaine: data.semaine || null,
      duration: data.duration || null,
      deadline: data.deadline || null,
      done: data.done ?? false,
    };
  }, []);

  const createTask = useCallback(async (data) => {
    const payload = normalizeTaskPayload(data);
    const res = await api.tasks.create(payload);
    const task = mapTask(res.task || res);
    setTasks(prev => [...prev, task]);
    return task;
  }, [normalizeTaskPayload]);

  const updateTask = useCallback(async (id, data) => {
    const payload = normalizeTaskPayload(data);
    const res = await api.tasks.update(id, payload);
    const task = mapTask(res.task || res);
    setTasks(prev => prev.map(t => t.id === id ? task : t));
    return task;
  }, [normalizeTaskPayload]);

  const toggleTask = useCallback(async (id) => {
    try {
      const res = await api.tasks.toggle(id);
      const task = mapTask(res.task || res);
      setTasks(prev => prev.map(t => t.id === id ? task : t));
    } catch {
      setTasks(prev => prev.map(t => t.id === id ? { ...t, done: !t.done } : t));
    }
  }, []);

  const deleteTask = useCallback(async (id) => {
    try {
      await api.tasks.delete(id);
    } catch (error) {
      console.warn('Suppression tâche via DELETE impossible, tentative de marquage:', error);
      try {
        await api.tasks.update(id, { done: true });
      } catch (fallbackError) {
        console.error('Échec suppression tâche:', fallbackError);
        throw fallbackError;
      }
    }
    setTasks(prev => prev.filter(t => t.id !== id));
  }, []);

  // CRUD Projets
  const createProject = useCallback(async (data) => {
    const res = await api.projects.create(data);
    const project = mapProject(res.project || res);
    setProjects(prev => [...prev, project]);
    return project;
  }, []);

  const updateProject = useCallback(async (id, data) => {
    const res = await api.projectsCRUD.update(id, data);
    const project = mapProject(res.project || res);
    setProjects(prev => prev.map(p => p.id === id ? project : p));
    return project;
  }, []);

  const deleteProject = useCallback(async (id) => {
    try { await api.projectsCRUD.delete(id); } catch {}
    setProjects(prev => prev.filter(p => p.id !== id));
  }, []);

  // CRUD Objectifs
  const createObjectif = useCallback(async (employeeId, data) => {
    const res = await api.objectifs.create({ employee_id: employeeId, ...data });
    const obj = res.objectif || res;
    setEmployes(prev => prev.map(e => e.id === employeeId
      ? { ...e, objectifs: [...e.objectifs, { 
          id: obj.id, 
          titre: obj.titre, 
          description: obj.description || '', 
          progression: obj.progression || 0, 
          statut: obj.statut || 'en_cours',
          commentaire: obj.commentaire || ''
        }] }
      : e
    ));
    return obj;
  }, []);

  const updateObjectif = useCallback(async (empId, objId, data) => {
    const res = await api.objectifs.update(objId, data);
    const obj = res.objectif || res;
    setEmployes(prev => prev.map(e => e.id === empId
      ? { ...e, objectifs: e.objectifs.map(o => o.id === objId ? { ...o, ...obj } : o) }
      : e
    ));
  }, []);

  const deleteObjectif = useCallback(async (empId, objId) => {
    try { await api.objectifs.delete(objId); } catch {}
    setEmployes(prev => prev.map(e => e.id === empId
      ? { ...e, objectifs: e.objectifs.filter(o => o.id !== objId) }
      : e
    ));
  }, []);

  return {
    employes, setEmployes,
    tasks, setTasks,
    projects, setProjects,
    loading, error,
    refetch: fetchData,
    createTask, updateTask, toggleTask, deleteTask,
    createProject, updateProject, deleteProject,
    createObjectif, updateObjectif, deleteObjectif,
  };
}